import { QueryValidatorCheck } from "./../helpers/query-validator";
import { LoggingService } from "../helpers/logging-service";
import { UtilService } from "../helpers/util-service";
import { FuseErrorUtilsService } from "../helpers/errors";
const keyConditionMap = {
    $eq: "=",
    $lt: "<",
    $lte: "<=",
    $gt: ">",
    $gte: ">=",
    $beginsWith: "",
    $between: "",
};
const conditionMapPre = {
    $ne: "<>",
    $exists: "",
    $in: "",
    $nin: "",
    $not: "",
    $contains: "",
    $notContains: "",
    $elemMatch: "",
    $nestedMatch: "",
};
const conditionMap = { ...keyConditionMap, ...conditionMapPre };
function hasQueryConditionValue(key) {
    if (key && Object.keys(conditionMap).includes(key) && conditionMap[key]) {
        return true;
    }
    return false;
}
const getRandom = () => [
    Math.round(Math.random() * 99999),
    Math.round(Math.random() * 88888),
    Math.round(Math.random() * 77777),
].join("");
export class DynamoFilterQueryOperation {
    operation__filterFieldExist({ fieldName }) {
        const attrKeyHash = `#attrKey1${getRandom()}`.toLowerCase();
        const result = {
            xExpressionAttributeNames: {
                [attrKeyHash]: fieldName,
            },
            xFilterExpression: `attribute_exists (${attrKeyHash})`,
        };
        return result;
    }
    operation__filterFieldNotExist({ fieldName }) {
        const attrKeyHash = `#attrKey2${getRandom()}`.toLowerCase();
        const result = {
            xExpressionAttributeNames: {
                [attrKeyHash]: fieldName,
            },
            xFilterExpression: `attribute_not_exists (${attrKeyHash})`,
        };
        return result;
    }
    operation__helperFilterBasic({ fieldName, val, conditionExpr, }) {
        const keyAttr = `:attr${fieldName}${getRandom()}`.toLowerCase();
        const attrKeyHash = `#attrKey3${getRandom()}`.toLowerCase();
        const result = {
            xExpressionAttributeValues: {
                [keyAttr]: val,
            },
            xExpressionAttributeNames: {
                [attrKeyHash]: fieldName,
            },
            xFilterExpression: [attrKeyHash, conditionExpr, keyAttr].join(" "),
        };
        return result;
    }
    operation__filterIn({ fieldName, attrValues }) {
        const expressAttrVal = {};
        const expressAttrName = {};
        const filterExpress = [];
        const _attrKeyHash = `#attrKey4${getRandom()}`.toLowerCase();
        expressAttrName[_attrKeyHash] = fieldName;
        attrValues.forEach((item) => {
            const keyAttr = `:attr${fieldName}${getRandom()}`.toLowerCase();
            expressAttrVal[keyAttr] = item;
            filterExpress.push(`${_attrKeyHash} = ${keyAttr}`);
        });
        const _filterExpression = filterExpress.join(" OR ").trim();
        const _filterExpressionValue = filterExpress.length > 1 ? `(${_filterExpression})` : _filterExpression;
        const result = {
            xExpressionAttributeValues: {
                ...expressAttrVal,
            },
            xExpressionAttributeNames: {
                ...expressAttrName,
            },
            xFilterExpression: _filterExpressionValue,
        };
        return result;
    }
    operation__filterElemMatch({ fieldName, attrValues, }) {
        const result = [];
        attrValues.$in.forEach((term) => {
            const query01 = this.operation__filterContains({ term, fieldName });
            result.push(query01);
        });
        return result;
    }
    operation__filterNestedMatchObject({ fieldName, attrValues, }) {
        const results = [];
        Object.entries(attrValues).forEach(([subFieldName, queryval]) => {
            let _queryValue;
            if (queryval && typeof queryval === "object") {
                _queryValue = { ...queryval };
            }
            else {
                _queryValue = { $eq: queryval };
            }
            Object.entries(_queryValue).forEach(([condKey, val]) => {
                const conditionKey = condKey;
                if (!Object.keys(keyConditionMap).includes(conditionKey)) {
                    throw FuseErrorUtilsService.fuse_helper_createFriendlyError(`Invalid query key: ${conditionKey} @ NestedMatchObject`);
                }
                const conditionExpr = keyConditionMap[conditionKey];
                const attrValue = `:attr${getRandom()}`.toLowerCase();
                const attrKeyHash = `#attrKey${subFieldName}${getRandom()}`.toLowerCase();
                const parentFieldName = `#attrKey${fieldName}${getRandom()}`.toLowerCase();
                if (conditionExpr) {
                    const result = {
                        xExpressionAttributeValues: {
                            [attrValue]: val,
                        },
                        xExpressionAttributeNames: {
                            [attrKeyHash]: subFieldName,
                            [parentFieldName]: fieldName,
                        },
                        xFilterExpression: [`${parentFieldName}.${attrKeyHash}`, conditionExpr, attrValue].join(" "),
                    };
                    results.push(result);
                }
                else {
                    if (conditionKey === "$between") {
                        const fromKey = `:fromKey0${getRandom()}`.toLowerCase();
                        const toKey = `:toKey0${getRandom()}`.toLowerCase();
                        if (!(Array.isArray(val) && val.length === 2)) {
                            throw FuseErrorUtilsService.fuse_helper_createFriendlyError("$between query must be an array of length 2");
                        }
                        const [fromVal, toVal] = val;
                        const result = {
                            xExpressionAttributeValues: {
                                [fromKey]: fromVal,
                                [toKey]: toVal,
                            },
                            xExpressionAttributeNames: {
                                [attrKeyHash]: subFieldName,
                                [parentFieldName]: fieldName,
                            },
                            xFilterExpression: [`${parentFieldName}.${attrKeyHash}`, "between", fromKey, "and", toKey].join(" "),
                        };
                        results.push(result);
                    }
                    else if (conditionKey === "$beginsWith") {
                        const result = {
                            xExpressionAttributeValues: {
                                [attrValue]: val,
                            },
                            xExpressionAttributeNames: {
                                [attrKeyHash]: subFieldName,
                                [parentFieldName]: fieldName,
                            },
                            xFilterExpression: `begins_with (${parentFieldName}.${attrKeyHash}, ${attrValue})`,
                        };
                        results.push(result);
                    }
                    else {
                        throw FuseErrorUtilsService.fuse_helper_createFriendlyError(`Query key: ${conditionKey} not currently supported`);
                    }
                }
            });
        });
        return results;
    }
    operation__filterContains({ fieldName, term }) {
        const attrKeyHash = `#attrKey5${getRandom()}`.toLowerCase();
        const keyAttr = `:attr${fieldName}${getRandom()}`.toLowerCase();
        const result = {
            xExpressionAttributeValues: {
                [keyAttr]: term,
            },
            xExpressionAttributeNames: {
                [attrKeyHash]: fieldName,
            },
            xFilterExpression: `contains (${attrKeyHash}, ${keyAttr})`,
        };
        return result;
    }
    operation__filterNot({ fieldName, selectorValues, }) {
        const selector = { ...selectorValues };
        const mConditions = [];
        Object.entries(selector).forEach(([conditionKey, conditionValue]) => {
            if (hasQueryConditionValue(conditionKey)) {
                const _conditionKey01 = conditionKey;
                if (_conditionKey01 === "$beginsWith") {
                    QueryValidatorCheck.beginWith(conditionValue);
                    const _queryConditions = this.operation__filterBeginsWith({
                        fieldName: fieldName,
                        term: conditionValue,
                    });
                    mConditions.push(_queryConditions);
                }
                else if (_conditionKey01 === "$between") {
                    QueryValidatorCheck.between(conditionValue);
                    const [from, to] = conditionValue;
                    const _queryConditions = this.operation__filterBetween({
                        fieldName: fieldName,
                        from,
                        to,
                    });
                    mConditions.push(_queryConditions);
                }
                else {
                    const conditionExpr = conditionMap[conditionKey];
                    if (conditionExpr) {
                        const _queryConditions = this.operation__helperFilterBasic({
                            fieldName: fieldName,
                            val: conditionValue,
                            conditionExpr: conditionExpr,
                        });
                        mConditions.push(_queryConditions);
                    }
                    else {
                        QueryValidatorCheck.throwQueryNotFound(conditionKey);
                    }
                }
            }
        });
        return mConditions;
    }
    operation__filterBetween({ fieldName, from, to, }) {
        const _attrKeyHash = `#attrKey6${getRandom()}`.toLowerCase();
        const _fromKey = `:fromKey${getRandom()}`.toLowerCase();
        const _toKey = `:toKey${getRandom()}`.toLowerCase();
        const result = {
            xExpressionAttributeValues: {
                [_fromKey]: from,
                [_toKey]: to,
            },
            xExpressionAttributeNames: {
                [_attrKeyHash]: fieldName,
            },
            xFilterExpression: [_attrKeyHash, "between", _fromKey, "and", _toKey].join(" "),
        };
        return result;
    }
    operation__filterBeginsWith({ fieldName, term }) {
        const _attrKeyHash = `#attrKey7${getRandom()}`.toLowerCase();
        const keyAttr = `:attr${fieldName}${getRandom()}`.toLowerCase();
        const result = {
            xExpressionAttributeValues: {
                [keyAttr]: term,
            },
            xExpressionAttributeNames: {
                [_attrKeyHash]: fieldName,
            },
            xFilterExpression: `begins_with (${_attrKeyHash}, ${keyAttr})`,
        };
        return result;
    }
    operation__translateAdvancedQueryOperation({ fieldName, queryObject, }) {
        const queryConditions = [];
        const notConditions = [];
        const orConditions = [];
        Object.entries(queryObject).forEach(([condKey, conditionValue]) => {
            const conditionKey = condKey;
            if (conditionValue !== undefined) {
                if (conditionKey === "$between") {
                    QueryValidatorCheck.between(conditionValue);
                    const [from, to] = conditionValue;
                    const _queryConditions = this.operation__filterBetween({
                        fieldName: fieldName,
                        from,
                        to,
                    });
                    queryConditions.push(_queryConditions);
                }
                else if (conditionKey === "$beginsWith") {
                    QueryValidatorCheck.beginWith(conditionValue);
                    const _queryConditions = this.operation__filterBeginsWith({
                        fieldName: fieldName,
                        term: conditionValue,
                    });
                    queryConditions.push(_queryConditions);
                }
                else if (conditionKey === "$contains") {
                    QueryValidatorCheck.contains(conditionValue);
                    const _queryConditions = this.operation__filterContains({
                        fieldName: fieldName,
                        term: conditionValue,
                    });
                    queryConditions.push(_queryConditions);
                }
                else if (conditionKey === "$in") {
                    QueryValidatorCheck.in_query(conditionValue);
                    const _queryConditions = this.operation__filterIn({
                        fieldName: fieldName,
                        attrValues: conditionValue,
                    });
                    queryConditions.push(_queryConditions);
                }
                else if (conditionKey === "$nin") {
                    QueryValidatorCheck.notIn(conditionValue);
                    const _queryConditions = this.operation__filterIn({
                        fieldName: fieldName,
                        attrValues: conditionValue,
                    });
                    _queryConditions.xFilterExpression = `NOT ${_queryConditions.xFilterExpression}`;
                    queryConditions.push(_queryConditions);
                }
                else if (conditionKey === "$elemMatch") {
                    QueryValidatorCheck.elemMatch(conditionValue);
                    const elemMatchConditions = this.operation__filterElemMatch({
                        fieldName: fieldName,
                        attrValues: conditionValue,
                    });
                    if (elemMatchConditions?.length) {
                        for (const _queryCondition of elemMatchConditions) {
                            orConditions.push(_queryCondition);
                        }
                    }
                }
                else if (conditionKey === "$nestedMatch") {
                    QueryValidatorCheck.nestedMatch(conditionValue);
                    const nestedMatchConditions = this.operation__filterNestedMatchObject({
                        fieldName: fieldName,
                        attrValues: conditionValue,
                    });
                    if (nestedMatchConditions?.length) {
                        for (const _queryCondition of nestedMatchConditions) {
                            queryConditions.push(_queryCondition);
                        }
                    }
                }
                else if (conditionKey === "$not") {
                    QueryValidatorCheck.not_query(conditionValue);
                    const _queryConditions = this.operation__filterNot({
                        fieldName: fieldName,
                        selectorValues: conditionValue,
                    });
                    if (_queryConditions?.length) {
                        for (const _queryCondition of _queryConditions) {
                            notConditions.push(_queryCondition);
                        }
                    }
                }
                else if (conditionKey === "$notContains") {
                    QueryValidatorCheck.notContains(conditionValue);
                    const _queryConditions = this.operation__filterContains({
                        fieldName: fieldName,
                        term: conditionValue,
                    });
                    _queryConditions.xFilterExpression = `NOT (${_queryConditions.xFilterExpression})`;
                    queryConditions.push(_queryConditions);
                }
                else if (conditionKey === "$exists") {
                    QueryValidatorCheck.exists(conditionValue);
                    if (String(conditionValue) === "true") {
                        const _queryConditions = this.operation__filterFieldExist({
                            fieldName: fieldName,
                        });
                        queryConditions.push(_queryConditions);
                    }
                    else if (String(conditionValue) === "false") {
                        const _queryConditions = this.operation__filterFieldNotExist({
                            fieldName: fieldName,
                        });
                        queryConditions.push(_queryConditions);
                    }
                }
                else {
                    const conditionExpr = conditionMap[conditionKey];
                    if (hasQueryConditionValue(conditionKey) && conditionExpr) {
                        const _queryConditions = this.operation__helperFilterBasic({
                            fieldName: fieldName,
                            val: conditionValue,
                            conditionExpr: conditionExpr,
                        });
                        queryConditions.push(_queryConditions);
                    }
                    else {
                        QueryValidatorCheck.throwQueryNotFound(conditionKey);
                    }
                }
            }
        });
        LoggingService.log(queryConditions);
        return { queryConditions, notConditions, orConditions };
    }
    operation_translateBasicQueryOperation({ fieldName, queryObject }) {
        const _queryConditions = this.operation__helperFilterBasic({
            fieldName: fieldName,
            val: queryObject,
            conditionExpr: "=",
        });
        return _queryConditions;
    }
    processQueryFilter({ queryDefs, projectionFields, }) {
        let AND_queryConditions = [];
        let OR_queryConditions = [];
        let NOT_queryConditions = [];
        let NOT_inside_OR_queryConditions = [];
        const AND_FilterExpressionArray = [];
        const OR_FilterExpressionArray = [];
        const NOT_FilterExpressionArray = [];
        const NOT_inside_OR_FilterExpressionArray = [];
        Object.keys(queryDefs).forEach((fieldName_Or_And) => {
            if (fieldName_Or_And === "$or") {
                const orKey = fieldName_Or_And;
                const orArray = queryDefs[orKey];
                QueryValidatorCheck.or_query(orArray);
                if (orArray && Array.isArray(orArray)) {
                    orArray.forEach((orQuery) => {
                        Object.keys(orQuery).forEach((fieldName) => {
                            const orQueryObjectOrValue = orQuery[fieldName];
                            if (orQueryObjectOrValue !== undefined) {
                                if (orQueryObjectOrValue && typeof orQueryObjectOrValue === "object") {
                                    const _orQueryCond = this.operation__translateAdvancedQueryOperation({
                                        fieldName,
                                        queryObject: orQueryObjectOrValue,
                                    });
                                    OR_queryConditions = [...OR_queryConditions, ..._orQueryCond.queryConditions];
                                    NOT_inside_OR_queryConditions = [...NOT_inside_OR_queryConditions, ..._orQueryCond.notConditions];
                                }
                                else {
                                    const _orQueryConditions = this.operation_translateBasicQueryOperation({
                                        fieldName,
                                        queryObject: orQueryObjectOrValue,
                                    });
                                    OR_queryConditions = [...OR_queryConditions, _orQueryConditions];
                                }
                            }
                        });
                    });
                }
            }
            else if (fieldName_Or_And === "$and") {
                const andKey = fieldName_Or_And;
                const andArray = queryDefs[andKey];
                QueryValidatorCheck.and_query(andArray);
                if (andArray && Array.isArray(andArray)) {
                    andArray.forEach((andQuery) => {
                        Object.keys(andQuery).forEach((fieldName) => {
                            const andQueryObjectOrValue = andQuery[fieldName];
                            if (andQueryObjectOrValue !== undefined) {
                                if (andQueryObjectOrValue && typeof andQueryObjectOrValue === "object") {
                                    const _andQueryCond = this.operation__translateAdvancedQueryOperation({
                                        fieldName,
                                        queryObject: andQueryObjectOrValue,
                                    });
                                    AND_queryConditions = [...AND_queryConditions, ..._andQueryCond.queryConditions];
                                    NOT_queryConditions = [...NOT_queryConditions, ..._andQueryCond.notConditions];
                                    OR_queryConditions = [...OR_queryConditions, ..._andQueryCond.orConditions];
                                }
                                else {
                                    const _andQueryConditions = this.operation_translateBasicQueryOperation({
                                        fieldName,
                                        queryObject: andQueryObjectOrValue,
                                    });
                                    AND_queryConditions = [...AND_queryConditions, _andQueryConditions];
                                }
                            }
                        });
                    });
                }
            }
            else {
                if (fieldName_Or_And) {
                    const fieldName2 = fieldName_Or_And;
                    const queryObjectOrValue = queryDefs[fieldName2];
                    if (queryObjectOrValue !== undefined) {
                        if (queryObjectOrValue && typeof queryObjectOrValue === "object") {
                            const _queryCond = this.operation__translateAdvancedQueryOperation({
                                fieldName: fieldName2,
                                queryObject: queryObjectOrValue,
                            });
                            AND_queryConditions = [...AND_queryConditions, ..._queryCond.queryConditions];
                            NOT_queryConditions = [...NOT_queryConditions, ..._queryCond.notConditions];
                            OR_queryConditions = [...OR_queryConditions, ..._queryCond.orConditions];
                        }
                        else {
                            const _queryConditions = this.operation_translateBasicQueryOperation({
                                fieldName: fieldName2,
                                queryObject: queryObjectOrValue,
                            });
                            AND_queryConditions = [...AND_queryConditions, _queryConditions];
                        }
                    }
                }
            }
        });
        let _expressionAttributeValues = {};
        let _expressionAttributeNames = {};
        let _projectionExpression = undefined;
        for (const item of AND_queryConditions) {
            _expressionAttributeNames = {
                ..._expressionAttributeNames,
                ...item.xExpressionAttributeNames,
            };
            _expressionAttributeValues = {
                ..._expressionAttributeValues,
                ...item.xExpressionAttributeValues,
            };
            AND_FilterExpressionArray.push(item.xFilterExpression);
        }
        for (const item2 of OR_queryConditions) {
            _expressionAttributeNames = {
                ..._expressionAttributeNames,
                ...item2.xExpressionAttributeNames,
            };
            _expressionAttributeValues = {
                ..._expressionAttributeValues,
                ...item2.xExpressionAttributeValues,
            };
            OR_FilterExpressionArray.push(item2.xFilterExpression);
        }
        for (const item3 of NOT_queryConditions) {
            _expressionAttributeNames = {
                ..._expressionAttributeNames,
                ...item3.xExpressionAttributeNames,
            };
            _expressionAttributeValues = {
                ..._expressionAttributeValues,
                ...item3.xExpressionAttributeValues,
            };
            NOT_FilterExpressionArray.push(item3.xFilterExpression);
        }
        for (const item4 of NOT_inside_OR_queryConditions) {
            _expressionAttributeNames = {
                ..._expressionAttributeNames,
                ...item4.xExpressionAttributeNames,
            };
            _expressionAttributeValues = {
                ..._expressionAttributeValues,
                ...item4.xExpressionAttributeValues,
            };
            NOT_inside_OR_FilterExpressionArray.push(item4.xFilterExpression);
        }
        let _andfilterExpression = "";
        let _orfilterExpression = "";
        let _notfilterExpression = "";
        let _notInsideOrFilterExpression = "";
        if (AND_FilterExpressionArray?.length) {
            _andfilterExpression = AND_FilterExpressionArray.join(" AND ").trim();
        }
        if (OR_FilterExpressionArray?.length) {
            _orfilterExpression = OR_FilterExpressionArray.join(" OR ").trim();
        }
        if (NOT_FilterExpressionArray?.length) {
            _notfilterExpression = NOT_FilterExpressionArray.join(" AND ").trim();
            _notfilterExpression = `NOT (${_notfilterExpression})`;
        }
        if (NOT_inside_OR_FilterExpressionArray?.length) {
            _notInsideOrFilterExpression = NOT_inside_OR_FilterExpressionArray.join(" OR ").trim();
            _notInsideOrFilterExpression = `NOT (${_notInsideOrFilterExpression})`;
        }
        let allFilters = [
            _andfilterExpression,
            _notfilterExpression,
            _orfilterExpression,
            _notInsideOrFilterExpression,
        ].filter((f) => f);
        if (allFilters?.length && allFilters.length > 1) {
            allFilters = allFilters.map((f) => `(${f})`);
        }
        const _filterExpression = allFilters.join(" AND ");
        if (projectionFields?.length && Array.isArray(projectionFields)) {
            const _projection_expressionAttributeNames = {};
            projectionFields.forEach((field) => {
                if (field && typeof field === "string") {
                    const attrKeyHash = `#attrKey8${getRandom()}`.toLowerCase();
                    _projection_expressionAttributeNames[attrKeyHash] = field;
                }
            });
            _projectionExpression = Object.keys(_projection_expressionAttributeNames).join(", ");
            _expressionAttributeNames = {
                ..._projection_expressionAttributeNames,
                ..._expressionAttributeNames,
            };
        }
        const _expressionAttributeValuesFinal = UtilService.objectHasAnyProperty(_expressionAttributeValues)
            ? _expressionAttributeValues
            : undefined;
        const _expressionAttributeNamesFinal = UtilService.objectHasAnyProperty(_expressionAttributeNames)
            ? _expressionAttributeNames
            : undefined;
        const queryExpressions = {
            expressionAttributeValues: _expressionAttributeValuesFinal,
            filterExpression: _filterExpression,
            projectionExpressionAttr: _projectionExpression,
            expressionAttributeNames: _expressionAttributeNamesFinal,
        };
        LoggingService.log(JSON.stringify({ queryExpressions }, null, 2));
        return queryExpressions;
    }
}
